import { useEffect, useRef, useState } from "react";
import { BarcodeDetectorPolyfill } from "@undecaf/barcode-detector-polyfill";
import { X } from "@phosphor-icons/react";

if (!("BarcodeDetector" in window)) {
  // eslint-disable-next-line no-undef
  window.BarcodeDetector = BarcodeDetectorPolyfill;
}

const SCAN_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"];
// The barcode must stay continuously detected for this long before it is accepted
// (2x the previous effective dwell) - prevents accidental scans when panning past products.
const SCAN_DWELL_MS = 1000;

/**
 * Full-screen camera-based barcode scanner. Uses the native BarcodeDetector API when the browser
 * supports it (e.g. Android Chrome), otherwise falls back to the @undecaf/barcode-detector-polyfill
 * WebAssembly (zbar) implementation - this matters because iOS Safari does not support the native
 * API at all, and a large share of mobile users are on iPhones.
 */
export default function BarcodeScannerModal({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const [error, setError] = useState("");
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let stream = null;
    let rafId = null;
    let candidate = null;
    let candidateSince = 0;
    setError("");
    setHolding(false);

    // eslint-disable-next-line no-undef
    const detector = new window.BarcodeDetector({ formats: SCAN_FORMATS });

    const scanLoop = async () => {
      if (cancelled) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2) {
        try {
          const barcodes = await detector.detect(video);
          if (barcodes.length > 0) {
            const value = barcodes[0].rawValue;
            const now = performance.now();
            if (value !== candidate) {
              candidate = value;
              candidateSince = now;
              setHolding(true);
            } else if (now - candidateSince >= SCAN_DWELL_MS) {
              setHolding(false);
              onDetected(value);
              return;
            }
          } else if (candidate) {
            candidate = null;
            setHolding(false);
          }
        } catch (e) {
          // transient decode error on a bad frame - keep scanning
        }
      }
      if (!cancelled) rafId = requestAnimationFrame(scanLoop);
    };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play();
        }
        rafId = requestAnimationFrame(scanLoop);
      })
      .catch(() => {
        setError("Camera access denied or unavailable. Please allow camera permission, or type the barcode manually below.");
      });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [open, onDetected]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black" data-testid="barcode-scanner-modal">
      <div className="flex items-center justify-between p-4">
        <p className="text-sm font-medium text-white">Scan Barcode</p>
        <button type="button" onClick={onClose} className="p-1 text-white" data-testid="barcode-scanner-close-button">
          <X size={22} />
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} muted playsInline className="h-full w-full object-cover" data-testid="barcode-scanner-video" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className={`h-1/3 w-4/5 rounded-lg border-2 ${holding ? "border-green-400" : "border-white/80"}`} />
        </div>
        {holding && (
          <div className="pointer-events-none absolute bottom-6 left-0 right-0 text-center">
            <span className="rounded-full bg-green-500/90 px-3 py-1 text-sm font-medium text-white" data-testid="barcode-hold-indicator">
              Hold steady...
            </span>
          </div>
        )}
      </div>
      {error && (
        <div className="p-4 text-center text-sm text-red-300" data-testid="barcode-scanner-error">
          {error}
        </div>
      )}
    </div>
  );
}
